import { apiService } from '../../services/api';
import type {
  DuplicateEntityType,
  DuplicateQuery,
  DuplicateScan,
  MatchHit,
  MatchProbe,
  MergePlan,
  MergePreview,
  MergeResult,
} from '../../types/duplicates';

const BASE = '/api/duplicates';

/** The backend on this server predates `/api/duplicates` — settings cannot fix that. */
export class DuplicatesUnavailableError extends Error {
  constructor(message = 'Duplicate detection is not available on this server yet.') {
    super(message);
    this.name = 'DuplicatesUnavailableError';
  }
}

/** One short line a reviewer can act on, never a raw stack or a bare status code */
export function duplicatesErrorMessage(error: any): string {
  if (error instanceof DuplicatesUnavailableError) return error.message;
  const data = error?.response?.data;
  // This API answers either { message } or { error: { message } }
  const body = data?.message ?? data?.error?.message;
  const raw = Array.isArray(body) ? body.join(', ') : body || error?.message || '';
  const text = String(raw).trim();
  const status = error?.response?.status;
  if (status === 401) return 'Your session has expired — sign in again';
  if (status === 403) return 'You do not have permission to do that';
  if (status === 409) return text && text.length <= 140 ? text : 'One of these records has already been merged';
  if (/network error|failed to fetch|load failed|timeout/i.test(text)) return 'Cannot reach the server — check your connection';
  if (status >= 500) return text && text.length <= 140 ? `Server error: ${text}` : 'The server could not complete that — try again';
  return text || 'Something went wrong';
}

/** A 404 on a collection endpoint means the module is not deployed, not "nothing found" */
function asUnavailable(error: any): never {
  if (error?.response?.status === 404) throw new DuplicatesUnavailableError();
  throw error;
}

/** Drops empty values so the query string stays readable and cache-friendly */
export function toParams(query: object): Record<string, string> {
  const params: Record<string, string> = {};
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params[key] = String(value);
  });
  return params;
}

const EMPTY_SCAN: DuplicateScan = {
  candidates: [],
  scanned: 0,
  compared: 0,
  generatedAt: new Date(0).toISOString(),
  durationMs: 0,
};

/* ------------------------------------------------------------------ */
/* The review queue                                                    */
/* ------------------------------------------------------------------ */

export async function scanDuplicates(query: DuplicateQuery = {}): Promise<DuplicateScan> {
  try {
    const res = await apiService.get<DuplicateScan>(BASE, { params: toParams(query) });
    const data = res.data as DuplicateScan | undefined;
    if (!data || !Array.isArray(data.candidates)) return EMPTY_SCAN;
    return data;
  } catch (error) {
    return asUnavailable(error);
  }
}

export async function dismissPair(pairId: string): Promise<void> {
  await apiService.post(`${BASE}/dismiss`, { pairId });
}

/* ------------------------------------------------------------------ */
/* Merging                                                             */
/* ------------------------------------------------------------------ */

export async function previewMerge(plan: MergePlan): Promise<MergePreview> {
  const res = await apiService.post<MergePreview>(`${BASE}/merge/preview`, plan);
  return res.data;
}

export async function mergeRecords(plan: MergePlan): Promise<MergeResult> {
  const res = await apiService.post<MergeResult>(`${BASE}/merge`, plan);
  return res.data;
}

export async function listMerges(): Promise<MergeResult[]> {
  try {
    const res = await apiService.get<MergeResult[]>(`${BASE}/merges`);
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    return asUnavailable(error);
  }
}

/** Where a record ended up after someone folded it into another */
export async function resolveEntity(
  entityType: DuplicateEntityType,
  id: string,
): Promise<{ survivorId: string }> {
  const res = await apiService.get<{ survivorId: string }>(`${BASE}/resolve/${entityType}/${id}`);
  return res.data;
}

/* ------------------------------------------------------------------ */
/* Matching one record before it is created                            */
/* ------------------------------------------------------------------ */

/**
 * Asks whether a record like this already exists. Callers sit in front of a
 * person waiting to save, so this never runs long: past `timeoutMs` it gives
 * up and answers "nothing found" rather than holding up the import.
 */
export async function probeMatches(probe: MatchProbe, timeoutMs = 4000): Promise<MatchHit[]> {
  const res = await apiService.post<MatchHit[]>(`${BASE}/probe`, probe, { timeout: timeoutMs });
  return Array.isArray(res.data) ? res.data : [];
}
